/*
 * Design: Gradient Noir – Contact page with form and contact info
 */
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useState, useRef } from "react";
import { useLocation } from "wouter";
import api from "@/lib/api";
import { getTrackingData } from "@/lib/utm";
import Turnstile from "@/components/Turnstile";

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const contactInfo = [
  { icon: Phone, label: "טלפון", value: "054-295-0091", dir: "ltr" as const },
  { icon: Mail, label: "אימייל", value: "info@upsite.co.il", dir: "ltr" as const },
  { icon: MapPin, label: "כתובת", value: "כ״ג יורדי הסירה 1, תל אביב", dir: "rtl" as const },
  { icon: Clock, label: "שעות פעילות", value: "א׳-ה׳ 09:00-18:00", dir: "rtl" as const },
];

export default function Contact() {
  usePageMeta({
    title: "מחפשים חברה שמתמחה בקידום אורגני? צרו קשר עם אפסייט עוד היום",
    description: "הצוות של UpSite מחכה לכם לשיחת ייעוץ לא מחייבת על קידום אורגני, בניית אתרים וצמיחה דיגיטלית. אל תשארו מאחור, השאירו פרטים ונחזור אליכם בהקדם",
  });

  const [submitting, setSubmitting] = useState(false);
  const [, setLocation] = useLocation();
  const turnstileToken = useRef<string | null>(null);
  const formTs = useRef(Date.now());

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!turnstileToken.current) {
      alert("אנא המתינו לאימות אבטחה.");
      return;
    }
    setSubmitting(true);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    try {
      await api.post("/contact-submissions", {
        name: data.get("name"),
        phone: data.get("phone"),
        email: data.get("email"),
        message: data.get("message"),
        turnstileToken: turnstileToken.current,
        website: data.get("website") || "",
        _formTs: formTs.current,
        ...getTrackingData("contact"),
      });
      form.reset();
      setLocation("/thank-you");
    } catch {
      alert("שגיאה בשליחת הטופס. נסו שוב.");
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-[Rubik] leading-tight">
              צריכים אתר או קידום שמביא תוצאות?{" "}
              <span className="gradient-text">דברו איתנו.</span>
            </h1>
            <p className="text-lg text-white/60 leading-relaxed max-w-2xl">
              נשמח לשמוע על הפרויקט שלכם ולהציע פתרונות מותאמים. מלאו את הטופס ונחזור אליכם בהקדם.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="py-20 lg:py-28 section-navy">
        <div className="container">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <AnimatedSection>
                <div className="glass-card p-8 lg:p-10">
                  <h2 className="text-2xl font-bold text-white mb-8 font-[Rubik]">
                    שלחו לנו הודעה
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot – hidden from humans, bots auto-fill it */}
                    <input
                      type="text"
                      name="website"
                      autoComplete="off"
                      tabIndex={-1}
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }}
                    />
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm text-white/60 mb-2">שם מלא</label>
                        <input
                          name="name"
                          type="text"
                          required
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/30 focus:border-[#66CDAA]/50 focus:ring-1 focus:ring-[#66CDAA]/30 outline-none transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-white/60 mb-2">טלפון</label>
                        <input
                          name="phone"
                          type="tel"
                          required
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/30 focus:border-[#66CDAA]/50 focus:ring-1 focus:ring-[#66CDAA]/30 outline-none transition-all"
                          dir="ltr"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-white/60 mb-2">אימייל</label>
                      <input
                        name="email"
                        type="email"
                        required
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/30 focus:border-[#66CDAA]/50 focus:ring-1 focus:ring-[#66CDAA]/30 outline-none transition-all"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/60 mb-2">הודעה</label>
                      <textarea
                        name="message"
                        rows={5}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/30 focus:border-[#66CDAA]/50 focus:ring-1 focus:ring-[#66CDAA]/30 outline-none transition-all resize-none"
                        placeholder="ספרו לנו על הפרויקט שלכם..."
                      />
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="privacy_consent"
                        required
                        className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-[#66CDAA] focus:ring-[#66CDAA]/30 accent-[#66CDAA] shrink-0"
                      />
                      <span className="text-sm text-white/50">
                        אני מסכים/ה לקבלת פניות ודיוור מאפסייט בהתאם ל<a href="/privacy" className="text-[#66CDAA] hover:underline">מדיניות הפרטיות</a>. ניתן לבטל בכל עת.
                      </span>
                    </label>
                    <Turnstile
                      onVerify={(token) => { turnstileToken.current = token; }}
                      onExpire={() => { turnstileToken.current = null; }}
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="glow-btn w-full text-base py-4 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      {submitting ? "שולח..." : "שלחו הודעה"}
                    </button>
                  </form>
                </div>
              </AnimatedSection>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <AnimatedSection delay={200}>
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-white mb-8 font-[Rubik]">
                    פרטי התקשרות
                  </h2>

                  {contactInfo.map((info, i) => (
                    <div key={i} className="glass-card p-5 flex gap-4 items-center">
                      <div className="w-12 h-12 rounded-xl bg-[#66CDAA]/10 flex items-center justify-center shrink-0">
                        <info.icon className="w-5 h-5 text-[#66CDAA]" />
                      </div>
                      <div>
                        <div className="text-sm text-white/40 mb-0.5">{info.label}</div>
                        <div className="text-white font-medium" dir={info.dir}>
                          {info.value}
                        </div>
                      </div>
                    </div>
                  ))}


                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
